export interface ISearchParams {
  order_by?: string
  order?: string
  term?: string
  template_id?: string
}

export type TypeParams = ISearchParams | string | null
export type StateFromQueryCallback = (params: TypeParams) => void

/**
 * A custom hook that sets search parameters in the URL;
 * and calls a callback function to set external search parameters.
 * @param {TypeParams} searchParams
 * @param {TypeParams} setSearchParams
 * @returns
 */
export const useQueryString = (searchParams?: any, setSearchParams?: (term) => void) => {
  const query = window.location.search
  const isSingleParam = typeof searchParams === 'string'
  const singleParamKey = 'q'

  /**
   * Calls a callback function that includes the search parameters or term.
   * - An example of the callback function could be for updating an API
   * call to include the search parameters.
   * @param {StateFromQueryCallback} cb Callback function.
   */
  function setStateFromQuery(cb: StateFromQueryCallback) {
    if (!query) return
    if (isSingleParam) {
      const searchTerm = new URLSearchParams(query).get(singleParamKey)
      cb(searchTerm)
    } else {
      const urlSearchParms = new URLSearchParams(query)
      const updatedUrlSearchParams = {}
      for (const [key, value] of urlSearchParms) {
        updatedUrlSearchParams[key] = value
      }
      cb(updatedUrlSearchParams)
    }
  }

  /**
   * Push history state changes that include URL Search Parameters.
   * @param {TypeParams} params
   */
  function setUrl(params) {
    const path = window.location.pathname

    if (!params) {
      setSearchParams?.('')
      window.history.pushState({}, '', path)
      return
    }

    const urlSearchParms = new URLSearchParams()
    if (isSingleParam) {
      if (params[singleParamKey]) {
        urlSearchParms.append(singleParamKey, params[singleParamKey])
      }
      setSearchParams?.(params[singleParamKey])
    } else {
      for (const key in params) {
        if (params[key]) {
          urlSearchParms.append(key, params[key])
        }
      }
      setSearchParams?.(params)
    }

    if (urlSearchParms.size === 0) {
      window.history.pushState({}, '', path)
      return
    }

    const queryString = `?${urlSearchParms.toString()}`
    const updatedUrl = `${path}${queryString}`
    window.history.pushState({}, '', encodeURI(updatedUrl))
  }

  return { setStateFromQuery, setUrl }
}
